import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { apiService } from '../services/apiService';

const PLAN_TYPES = ['FREE', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'] as const;
const BILLING_CYCLES = ['MONTHLY', 'YEARLY'] as const;

function formatLimit(n: number | undefined) {
  if (n == null) return '—';
  if (n < 0) return '∞';
  return String(n);
}

function effSummary(limits: Record<string, number> | undefined) {
  if (!limits) return '—';
  const p = limits.MAX_PROJECTS;
  const c = limits.MAX_COLLECTIONS;
  const r = limits.MAX_REQUESTS;
  return `Proj ${formatLimit(p)} · Col ${formatLimit(c)} · Req ${formatLimit(r)}`;
}

const SuperAdminPlanManagementPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [featureDefs, setFeatureDefs] = useState<any[]>([]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTenant, setAssignTenant] = useState<any>(null);
  const [assignForm, setAssignForm] = useState({
    planType: 'STANDARD',
    billingCycle: 'MONTHLY',
    licenseCount: '5',
  });

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planDialogMode, setPlanDialogMode] = useState<'create' | 'edit'>('create');
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [planForm, setPlanForm] = useState({
    planName: 'STANDARD',
    billingCycle: 'MONTHLY',
    price: '0',
    displayName: '',
    description: '',
    active: true,
    currency: 'USD',
    featureLimits: {} as Record<string, string>,
  });

  const [deletePlanId, setDeletePlanId] = useState<number | null>(null);

  const tenantQuery = useMemo(() => ({ search: tenantSearch.trim() || undefined }), [tenantSearch]);

  const loadFeatures = useCallback(async () => {
    try {
      const f = await apiService.getAdminPlanFeatureDefinitions();
      setFeatureDefs(f || []);
    } catch {
      setFeatureDefs([]);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminPlanManagementTenantOverview(tenantQuery);
      setTenants(data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [tenantQuery.search]);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminPlanCatalog();
      setPlans(data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  useEffect(() => {
    if (tab === 0) loadTenants();
  }, [tab, loadTenants]);

  useEffect(() => {
    if (tab === 1) loadPlans();
  }, [tab, loadPlans]);

  const openAssign = (t: any) => {
    setAssignTenant(t);
    setAssignForm({
      planType: t.planType || 'STANDARD',
      billingCycle: (t.billingCycle || 'YEARLY').toUpperCase(),
      licenseCount: String(t.licenseCount ?? 1),
    });
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    if (!assignTenant) return;
    setLoading(true);
    setError(null);
    try {
      await apiService.updateAdminTenantSubscription(assignTenant.tenantId, {
        planType: assignForm.planType,
        billingCycle: assignForm.billingCycle,
        licenseCount: Number(assignForm.licenseCount),
      });
      setAssignOpen(false);
      await loadTenants();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const openCreatePlan = () => {
    const initialLimits: Record<string, string> = {};
    featureDefs.forEach((f) => {
      initialLimits[f.featureKey] = '';
    });
    setPlanDialogMode('create');
    setEditingPlanId(null);
    setPlanForm({
      planName: 'STANDARD',
      billingCycle: 'MONTHLY',
      price: '0',
      displayName: '',
      description: '',
      active: true,
      currency: 'USD',
      featureLimits: initialLimits,
    });
    setPlanDialogOpen(true);
  };

  const openEditPlan = (p: any) => {
    const fl: Record<string, string> = {};
    featureDefs.forEach((f) => {
      const v = p.featureLimits?.[f.featureKey];
      fl[f.featureKey] = v != null ? String(v) : '';
    });
    setPlanDialogMode('edit');
    setEditingPlanId(p.id);
    setPlanForm({
      planName: p.planName,
      billingCycle: p.billingCycle,
      price: String(p.price ?? ''),
      displayName: p.displayName ?? '',
      description: p.description ?? '',
      active: p.active !== false,
      currency: p.currency || 'USD',
      featureLimits: fl,
    });
    setPlanDialogOpen(true);
  };

  const buildLimitsPayload = (): Record<string, number> => {
    const limits: Record<string, number> = {};
    featureDefs.forEach((f) => {
      const raw = planForm.featureLimits[f.featureKey];
      if (raw === undefined || raw === '') return;
      const n = parseInt(String(raw), 10);
      if (!Number.isNaN(n)) limits[f.featureKey] = n;
    });
    return limits;
  };

  const submitPlan = async () => {
    setLoading(true);
    setError(null);
    try {
      const limits = buildLimitsPayload();
      if (planDialogMode === 'create') {
        await apiService.createAdminPlanCatalog({
          planName: planForm.planName,
          billingCycle: planForm.billingCycle,
          price: parseFloat(planForm.price),
          displayName: planForm.displayName || undefined,
          description: planForm.description || undefined,
          active: planForm.active,
          currency: planForm.currency,
          featureLimits: Object.keys(limits).length ? limits : undefined,
        });
      } else if (editingPlanId != null) {
        await apiService.updateAdminPlanCatalog(editingPlanId, {
          price: parseFloat(planForm.price),
          displayName: planForm.displayName || undefined,
          description: planForm.description || undefined,
          active: planForm.active,
          currency: planForm.currency,
          featureLimits: Object.keys(limits).length ? limits : undefined,
        });
      }
      setPlanDialogOpen(false);
      await loadPlans();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeletePlan = async () => {
    if (deletePlanId == null) return;
    setLoading(true);
    setError(null);
    try {
      await apiService.deleteAdminPlanCatalog(deletePlanId);
      setDeletePlanId(null);
      await loadPlans();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SubscriptionsIcon fontSize="large" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Plan management
        </Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Review every tenant’s subscription, Stripe linkage, catalog pricing row, and effective feature limits. Maintain
        the plan catalog (price, currency, display) and per-plan feature caps. Plan names must match organization tiers:
        FREE, STANDARD, PROFESSIONAL, ENTERPRISE.
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Tenants & subscriptions" />
          <Tab label="Plan catalog" />
        </Tabs>
      </Paper>

      {loading && <CircularProgress size={22} sx={{ mb: 1 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              size="small"
              label="Search tenants"
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
            />
            <Button variant="outlined" onClick={() => loadTenants()} disabled={loading}>
              Reload
            </Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Licenses</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Catalog price</TableCell>
                <TableCell>Limits</TableCell>
                <TableCell>Stripe</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.tenantId}>
                  <TableCell>
                    <Typography fontWeight={600}>{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID {t.tenantId}
                    </Typography>
                  </TableCell>
                  <TableCell>{t.slug}</TableCell>
                  <TableCell>{t.status}</TableCell>
                  <TableCell>{t.planType}</TableCell>
                  <TableCell>{t.billingCycle || '—'}</TableCell>
                  <TableCell>{t.licenseCount ?? '—'}</TableCell>
                  <TableCell>
                    M {t.memberCount} · P {t.projectCount}
                    <br />
                    E {t.environmentCount} · C {t.collectionCount}
                  </TableCell>
                  <TableCell>
                    {t.catalogRowExists ? (
                      <>
                        <Chip size="small" color="success" label="Catalog" sx={{ mb: 0.5 }} />
                        <Typography variant="body2">
                          {(t.catalogCurrency || 'USD') + ' ' + Number(t.catalogPrice ?? 0).toFixed(2)}
                        </Typography>
                        {t.catalogDisplayName && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t.catalogDisplayName}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Chip size="small" label="No row" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: 'normal' }}>
                    <Tooltip title={JSON.stringify(t.effectiveFeatureLimits || {})}>
                      <span>{effSummary(t.effectiveFeatureLimits)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 140 }}>
                    <Typography variant="caption" noWrap display="block" title={t.stripeCustomerId || ''}>
                      {t.stripeCustomerId ? `Cust …${String(t.stripeCustomerId).slice(-6)}` : '—'}
                    </Typography>
                    <Typography variant="caption" noWrap display="block" title={t.stripeSubscriptionId || ''}>
                      {t.stripeSubscriptionId ? `Sub …${String(t.stripeSubscriptionId).slice(-6)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openAssign(t)}>
                      Assign plan
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Subscription catalog (per tier + billing cycle)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={() => loadPlans()} disabled={loading}>
                Reload
              </Button>
              <Button variant="contained" onClick={openCreatePlan}>
                Create plan
              </Button>
            </Box>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Cycle</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Display</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Feature limits</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>{p.planName}</TableCell>
                  <TableCell>{p.billingCycle}</TableCell>
                  <TableCell>
                    {(p.currency || 'USD') + ' ' + Number(p.price ?? 0).toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180, whiteSpace: 'normal' }}>
                    {p.displayName || '—'}
                  </TableCell>
                  <TableCell>{p.active === false ? 'No' : 'Yes'}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal', fontSize: 12 }}>
                    {p.featureLimits && Object.keys(p.featureLimits).length
                      ? Object.entries(p.featureLimits)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(', ')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openEditPlan(p)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => setDeletePlanId(p.id)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign subscription — {assignTenant?.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            select
            label="Plan type"
            value={assignForm.planType}
            onChange={(e) => setAssignForm((s) => ({ ...s, planType: e.target.value }))}
          >
            {PLAN_TYPES.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Billing cycle"
            value={assignForm.billingCycle}
            onChange={(e) => setAssignForm((s) => ({ ...s, billingCycle: e.target.value }))}
          >
            {BILLING_CYCLES.map((b) => (
              <MenuItem key={b} value={b}>
                {b}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="License seats"
            type="number"
            value={assignForm.licenseCount}
            onChange={(e) => setAssignForm((s) => ({ ...s, licenseCount: e.target.value }))}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAssign} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{planDialogMode === 'create' ? 'Create plan row' : 'Edit plan row'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {planDialogMode === 'create' && (
            <>
              <TextField
                select
                label="Plan tier"
                value={planForm.planName}
                onChange={(e) => setPlanForm((s) => ({ ...s, planName: e.target.value }))}
              >
                {PLAN_TYPES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Billing cycle"
                value={planForm.billingCycle}
                onChange={(e) => setPlanForm((s) => ({ ...s, billingCycle: e.target.value }))}
              >
                {BILLING_CYCLES.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          {planDialogMode === 'edit' && (
            <Typography color="text.secondary">
              {planForm.planName} / {planForm.billingCycle} (tier and cycle are fixed; delete and recreate to change.)
            </Typography>
          )}
          <TextField
            label="Price (major units, e.g. USD)"
            type="number"
            value={planForm.price}
            onChange={(e) => setPlanForm((s) => ({ ...s, price: e.target.value }))}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <TextField
            label="Currency"
            value={planForm.currency}
            onChange={(e) => setPlanForm((s) => ({ ...s, currency: e.target.value.toUpperCase() }))}
          />
          <TextField
            label="Display name"
            value={planForm.displayName}
            onChange={(e) => setPlanForm((s) => ({ ...s, displayName: e.target.value }))}
          />
          <TextField
            label="Description"
            value={planForm.description}
            onChange={(e) => setPlanForm((s) => ({ ...s, description: e.target.value }))}
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={planForm.active}
                onChange={(e) => setPlanForm((s) => ({ ...s, active: e.target.checked }))}
              />
            }
            label="Active in catalog"
          />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Feature limits (-1 = unlimited)
          </Typography>
          {featureDefs.map((f) => (
            <TextField
              key={f.featureKey}
              size="small"
              label={f.featureKey}
              helperText={f.description}
              value={planForm.featureLimits[f.featureKey] ?? ''}
              onChange={(e) =>
                setPlanForm((s) => ({
                  ...s,
                  featureLimits: { ...s.featureLimits, [f.featureKey]: e.target.value },
                }))
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitPlan} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deletePlanId != null} onClose={() => setDeletePlanId(null)}>
        <DialogTitle>Delete plan row?</DialogTitle>
        <DialogContent>
          <Typography>This removes catalog pricing and stored feature limits for this tier/cycle. Continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlanId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeletePlan} disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminPlanManagementPage;
